import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { ParteProduccionService } from '../services/ParteProduccionService';
import type {
  PresentacionTipoB,
  ProductoTipoBGrid,
  ProductoTipoBCelda,
  ProductoTerminadoTipoB,
} from '../types';

// Interface para devoluciones internas desde productos terminados A
export interface DevolucionInternaInfo {
  idGuiaProduccionDetalle: number;
  cantidadDevolucionInterna: number;
  pesoEmpaqueInterior: number; // Para calcular el total de peso
}

interface ProductoTipoBSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelectProducts: (selectedProducts: ProductoTerminadoTipoB[]) => void;
  parteId?: number; // Optional parteId for edit mode
  devolucionesInternas?: DevolucionInternaInfo[]; // Devoluciones internas de productos terminados A
}

interface CeldaConCantidad extends ProductoTipoBCelda {
  cantidadSeleccionada: number;
  cantidadMaxima: number; // Máximo permitido basado en devoluciones internas
}

const ProductoTipoBSelectionModal: React.FC<ProductoTipoBSelectionModalProps> = ({
  open,
  onClose,
  onSelectProducts,
  parteId,
  devolucionesInternas = [],
}) => {
  const [presentaciones, setPresentaciones] = useState<PresentacionTipoB[]>([]);
  const [selectedPresentacion, setSelectedPresentacion] = useState<number | null>(null);
  const [grid, setGrid] = useState<ProductoTipoBGrid | null>(null);
  const [celdasConCantidad, setCeldasConCantidad] = useState<CeldaConCantidad[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Total de devoluciones internas calculado para la presentación seleccionada (puede usarse para debugging)
  const [_totalDevolucionInterna, setTotalDevolucionInterna] = useState<number>(0);
  // Totales de devoluciones internas por presentación (calculado al cargar)
  const [totalesPorPresentacion, setTotalesPorPresentacion] = useState<Map<number, number>>(new Map());

  const loadPresentaciones = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await ParteProduccionService.getPresentacionesTipoB(parteId);
      setPresentaciones(result);

      // Si hay devoluciones internas, calcular los totales por presentación
      if (devolucionesInternas.length > 0) {
        const totalesMap = new Map<number, number>();

        // Cargar productos de cada presentación para calcular totales
        await Promise.all(result.map(async (presentacion) => {
          try {
            const grid = await ParteProduccionService.getProductosTipoBByPresentacion(
              presentacion.idPresentacion,
              parteId
            );

            let totalDevInt = 0;
            grid.celdas.forEach(celda => {
              celda.detalles.forEach(detalle => {
                const devolucion = devolucionesInternas.find(
                  d => d.idGuiaProduccionDetalle === detalle.idGuiaProduccionDetalle
                );
                if (devolucion) {
                  const cantidadMaxima = Math.min(
                    devolucion.cantidadDevolucionInterna,
                    detalle.cantidadDisponible
                  );
                  totalDevInt += cantidadMaxima * celda.pesoEmpaqueInterior;
                }
              });
            });

            totalesMap.set(presentacion.idPresentacion, totalDevInt);
          } catch (err) {
            console.error(`Error loading products for presentacion ${presentacion.idPresentacion}:`, err);
          }
        }));

        setTotalesPorPresentacion(totalesMap);
      }
    } catch (err) {
      setError('Error al cargar las presentaciones');
      console.error('Error loading presentaciones:', err);
    } finally {
      setLoading(false);
    }
  }, [parteId, devolucionesInternas]);

  const loadProductsByPresentacion = useCallback(async (idPresentacion: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await ParteProduccionService.getProductosTipoBByPresentacion(idPresentacion, parteId);
      setGrid(result);

      // Si hay devoluciones internas del form, usar esas cantidades; si no, usar cantidadDisponible del backend
      const hayDevoluciones = devolucionesInternas.length > 0;
      let totalDevInt = 0;

      const celdasInit = result.celdas.map(celda => {
        if (hayDevoluciones) {
          // Modo devolución interna: usar solo las cantidades marcadas en el formulario
          let cantidadFromDevoluciones = 0;
          celda.detalles.forEach(detalle => {
            const devolucion = devolucionesInternas.find(
              d => d.idGuiaProduccionDetalle === detalle.idGuiaProduccionDetalle
            );
            if (devolucion) {
              cantidadFromDevoluciones += devolucion.cantidadDevolucionInterna;
            }
          });

          const cantidadMaxima = Math.min(cantidadFromDevoluciones, celda.cantidadDisponible);
          totalDevInt += cantidadMaxima * celda.pesoEmpaqueInterior;

          return {
            ...celda,
            cantidadMaxima,
            cantidadSeleccionada: cantidadMaxima,
          };
        } else {
          // Modo normal: usar cantidadDisponible del backend (productos B ya guardados)
          return {
            ...celda,
            cantidadMaxima: celda.cantidadDisponible,
            cantidadSeleccionada: celda.cantidadDisponible,
          };
        }
      });

      setCeldasConCantidad(celdasInit);
      setTotalDevolucionInterna(totalDevInt);
    } catch (err) {
      setError('Error al cargar los productos');
      console.error('Error loading products:', err);
    } finally {
      setLoading(false);
    }
  }, [parteId, devolucionesInternas]);

  const handlePresentacionSelect = (idPresentacion: number) => {
    setSelectedPresentacion(idPresentacion);
  };

  const handleCantidadChange = (calibre: string, empaque: string, newValue: string) => {
    const isSaldoColumn = empaque.toLowerCase().includes('saldo');

    // Permitir campo vacío para que el usuario pueda borrar los ceros
    if (newValue === '' || newValue === '-') {
      setCeldasConCantidad(prev =>
        prev.map(celda => {
          if (celda.calibre === calibre && celda.empaque === empaque) {
            return { ...celda, cantidadSeleccionada: 0 };
          }
          return celda;
        })
      );
      return;
    }

    const cantidad = isSaldoColumn ? parseFloat(newValue) : parseInt(newValue);
    if (isNaN(cantidad)) return;

    setCeldasConCantidad(prev =>
      prev.map(celda => {
        if (celda.calibre === calibre && celda.empaque === empaque) {
          const maxCantidad = celda.cantidadMaxima;
          return {
            ...celda,
            cantidadSeleccionada: Math.min(Math.max(0, cantidad), maxCantidad),
          };
        }
        return celda;
      })
    );
  };

  const handleConfirmSelection = () => {
    const selectedProducts: ProductoTerminadoTipoB[] = [];
    const presentacionDesc = grid?.presentacionDescripcion || 'Sin Presentación';

    celdasConCantidad.forEach(celda => {
      if (celda.cantidadSeleccionada > 0 && celda.detalles.length > 0) {
        // Distribute the selected quantity among available detalles
        let remainingQty = celda.cantidadSeleccionada;

        for (const detalle of celda.detalles) {
          if (remainingQty <= 0) break;

          const qtyFromThisDetalle = Math.min(remainingQty, detalle.cantidadDisponible);
          if (qtyFromThisDetalle > 0) {
            selectedProducts.push({
              id: detalle.idGuiaProduccionDetalle,
              idGuiaProduccion: detalle.idGuiaProduccion,
              codigo: detalle.idArticulo, // Código del artículo
              numeroDerc: detalle.numeroDerc,
              duenio: detalle.duenio,
              encargado: detalle.encargado,
              fechaExtraccion: detalle.fechaExtraccion,
              producto: `${detalle.descripcion} - ${celda.empaque}`,
              especie: presentacionDesc,
              cantidad: qtyFromThisDetalle,
              peso: celda.pesoEmpaqueInterior,
              total: qtyFromThisDetalle * celda.pesoEmpaqueInterior,
            });
            remainingQty -= qtyFromThisDetalle;
          }
        }
      }
    });

    onSelectProducts(selectedProducts);
    handleClose();
  };

  const handleClose = () => {
    setSelectedPresentacion(null);
    setGrid(null);
    setCeldasConCantidad([]);
    setError(null);
    onClose();
  };

  const getTotalSelectedWeight = () => {
    return celdasConCantidad.reduce((total, celda) => {
      return total + celda.cantidadSeleccionada * celda.pesoEmpaqueInterior;
    }, 0);
  };

  const getTotalSelectedCount = () => {
    return celdasConCantidad.reduce((total, celda) => total + celda.cantidadSeleccionada, 0);
  };

  const getTotalsByEmpaque = () => {
    const totals: Record<string, number> = {};
    if (!grid) return totals;

    grid.empaques.forEach(empaque => {
      totals[empaque] = 0;
    });

    celdasConCantidad.forEach(celda => {
      if (celda.cantidadSeleccionada > 0) {
        const pesoTotal = celda.cantidadSeleccionada * celda.pesoEmpaqueInterior;
        totals[celda.empaque] = (totals[celda.empaque] || 0) + pesoTotal;
      }
    });

    return totals;
  };

  const getTotalsByCalibre = () => {
    const totals: Record<string, number> = {};
    if (!grid) return totals;

    grid.calibres.forEach(calibre => {
      totals[calibre] = 0;
    });

    celdasConCantidad.forEach(celda => {
      if (celda.cantidadSeleccionada > 0) {
        const pesoTotal = celda.cantidadSeleccionada * celda.pesoEmpaqueInterior;
        totals[celda.calibre] = (totals[celda.calibre] || 0) + pesoTotal;
      }
    });

    return totals;
  };

  const getCelda = (calibre: string, empaque: string): CeldaConCantidad | undefined => {
    return celdasConCantidad.find(c => c.calibre === calibre && c.empaque === empaque);
  };

  // Load presentaciones when modal opens
  useEffect(() => {
    if (open) {
      loadPresentaciones();
    }
  }, [open, loadPresentaciones]);

  // Load products when presentacion is selected
  useEffect(() => {
    if (selectedPresentacion) {
      loadProductsByPresentacion(selectedPresentacion);
    }
  }, [selectedPresentacion, loadProductsByPresentacion]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          height: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="h6" component="div" fontWeight={600}>
            Seleccionar Productos B y Devolución Interna
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Productos tipo B y devoluciones internas disponibles
          </Typography>
        </Box>
        <Button
          onClick={handleClose}
          size="small"
          color="inherit"
          sx={{ minWidth: 'auto', p: 0.5 }}
        >
          <CloseIcon fontSize="small" />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, display: 'flex', gap: 2, overflow: 'hidden' }}>
        {/* Left Panel - Presentaciones */}
        <Paper sx={{ width: 300, maxHeight: '100%', overflow: 'auto' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Presentaciones Disponibles
            </Typography>
          </Box>

          {loading && !selectedPresentacion && (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={24} />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && presentaciones.length === 0 && (
            <Box p={3}>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                No hay productos tipo B disponibles
              </Typography>
            </Box>
          )}

          <List sx={{ p: 0 }}>
            {presentaciones.map((presentacion) => {
              const isSelected = selectedPresentacion === presentacion.idPresentacion;
              // Usar el total calculado de devoluciones internas si existe
              const totalCalculado = totalesPorPresentacion.get(presentacion.idPresentacion);
              const hayDevolucionesParaEsta = totalCalculado !== undefined && totalCalculado > 0;
              const totalToShow = hayDevolucionesParaEsta
                ? totalCalculado
                : (devolucionesInternas.length > 0 ? 0 : presentacion.totalDisponible);

              return (
              <ListItem key={presentacion.idPresentacion} sx={{ p: 0 }}>
                <ListItemButton
                  selected={isSelected}
                  onClick={() => handlePresentacionSelect(presentacion.idPresentacion)}
                  sx={{
                    borderLeft: isSelected ? 3 : 0,
                    borderColor: 'primary.main',
                  }}
                >
                  <ListItemText
                    primary={presentacion.descripcion}
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Chip
                          label={presentacion.esFresco ? 'Fresco' : 'Procesado'}
                          color={presentacion.esFresco ? 'success' : 'info'}
                          size="small"
                        />
                        <Chip
                          label={`${totalToShow.toFixed(2)} kg`}
                          variant="outlined"
                          size="small"
                          color={hayDevolucionesParaEsta ? 'warning' : 'default'}
                        />
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
              );
            })}
          </List>
        </Paper>

        {/* Right Panel - Products Grid */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {!selectedPresentacion && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              flexDirection="column"
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Seleccione una presentación
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Elija una presentación de la lista para ver los productos disponibles
              </Typography>
            </Box>
          )}

          {selectedPresentacion && loading && (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
            </Box>
          )}

          {selectedPresentacion && !loading && grid && grid.calibres.length > 0 && (
            <Box>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Productos Disponibles - {grid.presentacionDescripcion}
                </Typography>
                {getTotalSelectedCount() > 0 && (
                  <Chip
                    label={`${getTotalSelectedCount()} unidades - ${getTotalSelectedWeight().toFixed(2)} kg`}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>

              <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Calibre</TableCell>
                      {grid.empaques.map((empaque) => (
                        <TableCell key={empaque} align="center" sx={{ fontWeight: 600, minWidth: 150 }}>
                          {empaque}
                        </TableCell>
                      ))}
                      <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'warning.light', minWidth: 120 }}>
                        Total por Calibre (kg)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {grid.calibres.map((calibre) => {
                      const totalsByCalibre = getTotalsByCalibre();
                      const totalCalibre = totalsByCalibre[calibre] || 0;

                      return (
                        <TableRow key={calibre} hover>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.50' }}>
                            {calibre}
                          </TableCell>
                          {grid.empaques.map((empaque) => {
                            const celda = getCelda(calibre, empaque);
                            // Mostrar si hay productos disponibles (devolución interna del form actual o productos B del backend)
                            const hasProducts = celda && celda.cantidadMaxima > 0;
                            const isSaldoColumn = empaque.toLowerCase().includes('saldo');

                            return (
                              <TableCell key={empaque} align="center">
                                {hasProducts ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                    <TextField
                                      size="small"
                                      value={celda.cantidadSeleccionada === 0 ? '' : celda.cantidadSeleccionada}
                                      onChange={(e) => handleCantidadChange(calibre, empaque, e.target.value)}
                                      onFocus={(e) => e.target.select()}
                                      placeholder="0"
                                      sx={{ width: 80 }}
                                      inputProps={{
                                        min: 0,
                                        max: celda.cantidadMaxima,
                                        type: 'number',
                                        step: isSaldoColumn ? 0.01 : 1,
                                        style: { textAlign: 'center' },
                                      }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                      Máx: {celda.cantidadMaxima}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.disabled">
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                            );
                          })}
                          <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'warning.light' }}>
                            {totalCalibre.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* Fila de totales de peso por empaque */}
                    <TableRow sx={{ bgcolor: 'primary.light', '& td': { borderTop: 2, borderColor: 'primary.main' } }}>
                      <TableCell sx={{ fontWeight: 700, color: 'primary.contrastText' }}>
                        PESO TOTAL (kg)
                      </TableCell>
                      {grid.empaques.map((empaque) => {
                        const totalsByEmpaque = getTotalsByEmpaque();
                        const pesoTotal = totalsByEmpaque[empaque] || 0;

                        return (
                          <TableCell key={empaque} align="center" sx={{ fontWeight: 700, color: 'primary.contrastText' }}>
                            {pesoTotal.toFixed(2)}
                          </TableCell>
                        );
                      })}
                      <TableCell
                        align="center"
                        sx={{
                          fontWeight: 700,
                          bgcolor: 'secondary.main',
                          color: 'secondary.contrastText',
                          fontSize: '1.1rem',
                          border: 3,
                          borderColor: 'secondary.dark'
                        }}
                      >
                        {getTotalSelectedWeight().toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {selectedPresentacion && !loading && grid && grid.calibres.length === 0 && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              flexDirection="column"
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No hay productos disponibles
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No se encontraron productos tipo B disponibles para esta presentación
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          color="inherit"
          variant="outlined"
          sx={{ fontWeight: 500 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirmSelection}
          variant="contained"
          color="primary"
          disabled={getTotalSelectedCount() === 0}
          sx={{ fontWeight: 600 }}
        >
          Confirmar Selección ({getTotalSelectedCount()} unidades - {getTotalSelectedWeight().toFixed(2)} kg)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductoTipoBSelectionModal;
